import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function getToken() {
  return localStorage.getItem("token");
}

export default function CityQueuePanel() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/discovery/priority-queue?limit=50`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setCities(res.data || []);
    } catch (err) {
      console.error("Priority queue fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${API}/discovery/priority-queue/recalculate`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      await fetchQueue();
    } catch (err) {
      console.error("Recalculate error:", err);
    } finally {
      setRecalculating(false);
    }
  };

  const handlePin = async (name, isPinned) => {
    try {
      const action = isPinned ? "unpin" : "pin";
      await axios.post(`${API}/discovery/priority-queue/${action}/${encodeURIComponent(name)}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      await fetchQueue();
    } catch (err) {
      console.error("Pin/unpin error:", err);
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-400 py-4 text-center">Loading city queue...</div>;
  }

  const top5 = new Set(cities.slice(0, 5).map(c => c.name));

  return (
    <div className="space-y-3" data-testid="city-queue-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">City Priority Queue</h3>
        <button
          onClick={handleRecalculate}
          disabled={recalculating}
          className="text-[10px] font-bold bg-slate-800 text-white px-3 py-1 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition"
          data-testid="recalculate-btn"
        >
          {recalculating ? "Recalculating..." : "Recalculate Now"}
        </button>
      </div>

      {cities.length === 0 ? (
        <p className="text-[11px] text-slate-400">No cities in queue. All priorities need calculation.</p>
      ) : (
        <div className="max-h-[360px] overflow-y-auto rounded-xl border border-slate-200">
          <table className="w-full text-[10px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">#</th>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">City</th>
                <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Score</th>
                <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Demand</th>
                <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Search</th>
                <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Fresh</th>
                <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Pop</th>
                <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Prox</th>
                <th className="text-center px-2 py-1.5 font-semibold text-slate-500">Pin</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((c, i) => {
                const s = c.priority_signals || {};
                const isTop5 = top5.has(c.name);
                const pinned = c.manually_pinned;
                return (
                  <tr
                    key={c.name}
                    className={`border-t border-slate-100 ${isTop5 ? "bg-amber-50" : ""} ${pinned ? "bg-sky-50" : ""}`}
                    data-testid={`queue-city-${c.name}`}
                  >
                    <td className="px-2 py-1.5 text-slate-400 font-mono">{i + 1}</td>
                    <td className="px-2 py-1.5 font-bold text-slate-700 truncate max-w-[120px]">
                      {pinned && <span className="text-sky-500 mr-1" title="Pinned">*</span>}
                      {c.name}
                      {c.state && <span className="text-slate-400 font-normal ml-1">{c.state}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800" data-testid={`score-${c.name}`}>
                      {c.priority_score?.toFixed(1) || "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{s.user_demand?.toFixed(0) || 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{s.search_intent?.toFixed(0) || 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{s.freshness_gap?.toFixed(0) || 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{s.population?.toFixed(0) || 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{s.proximity?.toFixed(0) || 0}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => handlePin(c.name, pinned)}
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${pinned ? "bg-sky-200 text-sky-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                        data-testid={`pin-btn-${c.name}`}
                      >
                        {pinned ? "Unpin" : "Pin"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
